import {
  FlowBlockValueFieldSchema,
  FlowBlockValueFieldSchemaVariant,
  FlowBlockValueTemplateDurationSchema,
  FlowConversationBlockMode,
  FlowConversationTagEditMode,
  FlowDurationUnits,
  FlowVersionTarget,
} from 'flow-schema';
import { z } from 'zod';

import { isDefined, isTruthy } from '../../../utils.js';
import { FlowArgumentSchema } from '../subFlow/v2/flowArgument.js';

const TagsArraySchema = z
  .array(z.string().min(1))
  .refine((tags) => new Set(tags).size === tags.length, {
    message: 'All items must be unique, no duplicate values allowed',
  });

const TagsExpressionSchema = FlowBlockValueFieldSchema({
  variant: FlowBlockValueFieldSchemaVariant.OptionalTemplate,
});

const TagsConfigSchema = z.union([TagsArraySchema, TagsExpressionSchema]);

const maximumTimeoutDuration = {
  value: 30,
  units: FlowDurationUnits.Days,
};
export const maximumNoPatientResponseTimeoutDuration = { ...maximumTimeoutDuration };

export const CreateConversationConfigSchema = z.object({
  agentPhoneNumber: z.string().optional(),
  callerPhoneNumberId: z.uuid().nullish(),
  firstMessage: z.string().min(1),
  tags: TagsConfigSchema,
  timeout: FlowBlockValueTemplateDurationSchema({
    pathName: 'timeout',
    maxDuration: maximumTimeoutDuration,
  }),
  noPatientResponseTimeout: FlowBlockValueTemplateDurationSchema({
    pathName: 'noPatientResponseTimeout',
    maxDuration: maximumNoPatientResponseTimeoutDuration,
  }).optional(),
  priority: z.string().optional(),
  patientReplyHandler: z
    .object({
      flowDefinitionId: z.string(),
      versionTarget: z.enum(FlowVersionTarget),
      flowId: z.string(),
      metadata: z.array(FlowArgumentSchema).optional(),
    })
    .optional(),
  mode: z.literal(FlowConversationBlockMode.Create),
  assignmentGroupId: z.string().optional(),
});
export type CreateConversationConfig = z.infer<typeof CreateConversationConfigSchema>;

export const ReopenConversationConfigSchema = z.object({
  existingConversationId: z.string().min(1),
  firstMessage: z.string().optional(),
  timeout: FlowBlockValueTemplateDurationSchema({
    pathName: 'timeout',
    maxDuration: maximumTimeoutDuration,
  }),
  noPatientResponseTimeout: FlowBlockValueTemplateDurationSchema({
    pathName: 'noPatientResponseTimeout',
    maxDuration: maximumNoPatientResponseTimeoutDuration,
  }).optional(),
  mode: z.literal(FlowConversationBlockMode.Reopen),
});
export type ReopenConversationConfig = z.infer<typeof ReopenConversationConfigSchema>;

export const ReplyConversationConfigSchema = z.object({
  existingConversationId: z.string().min(1),
  firstMessage: z.string().min(1),
  mode: z.literal(FlowConversationBlockMode.Reply),
});
export type ReplyConversationConfig = z.infer<typeof ReplyConversationConfigSchema>;

export const CloseConversationConfigSchema = z.object({
  existingConversationId: z.string().min(1),
  firstMessage: z.string().optional(),
  mode: z.literal(FlowConversationBlockMode.Close),
  resolutionReason: z.string().optional(),
  resolutionDetails: z.record(z.string(), z.unknown()).optional(),
});
export type CloseConversationConfig = z.infer<typeof CloseConversationConfigSchema>;

export const UpdateConversationConfigSchema = z.object({
  existingConversationId: z.string().min(1),
  mode: z.literal(FlowConversationBlockMode.Update),
  tagEditMode: z.enum(FlowConversationTagEditMode),
  tagsToAdd: z.array(z.string().min(1)).optional().nullable(),
  tagsToRemove: z.array(z.string().min(1)).optional().nullable(),
  tags: TagsConfigSchema.optional().nullable(),
  assignmentGroupId: z.string().optional(),
});
export type UpdateConversationConfig = z.infer<typeof UpdateConversationConfigSchema>;

/**
 * @deprecated use mode create, reopen, reply or close
 */
export const BaseConversationBlockConfigSchemaDeprecated = z.object({
  agentPhoneNumber: z.string().optional(),
  callerPhoneNumberId: z.uuid().nullish(),
  firstMessage: z.string(),
  tags: TagsArraySchema,
  timeout: FlowBlockValueTemplateDurationSchema({
    pathName: 'timeout',
    maxDuration: maximumTimeoutDuration,
  }),
  priority: z.string().optional(),
  existingConversationId: z.string().optional(),
  patientReplyHandler: z
    .object({
      flowDefinitionId: z.string(),
      versionTarget: z.enum(FlowVersionTarget),
      flowId: z.string(),
      metadata: z.array(FlowArgumentSchema).optional(),
    })
    .optional(),
});

export const WaitUntilClosedConversationBlockConfigSchema =
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  BaseConversationBlockConfigSchemaDeprecated.extend({
    mode: z
      .literal(FlowConversationBlockMode.WaitUntilClosed)
      .optional()
      .default(FlowConversationBlockMode.WaitUntilClosed),
  });

export const NonBlockingConversationBlockConfigSchema =
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  BaseConversationBlockConfigSchemaDeprecated.extend({
    mode: z.literal(FlowConversationBlockMode.NonBlocking),
  });

export const ConversationBlockConfigSchema = z
  .discriminatedUnion('mode', [
    CreateConversationConfigSchema,
    ReopenConversationConfigSchema,
    ReplyConversationConfigSchema,
    CloseConversationConfigSchema,
    UpdateConversationConfigSchema,
    WaitUntilClosedConversationBlockConfigSchema,
    NonBlockingConversationBlockConfigSchema,
  ])
  .refine(
    (config) => {
      if (
        config.mode === FlowConversationBlockMode.Create ||
        config.mode === FlowConversationBlockMode.WaitUntilClosed ||
        config.mode === FlowConversationBlockMode.NonBlocking
      ) {
        return isDefined(config.callerPhoneNumberId) || isTruthy(config.agentPhoneNumber);
      }
      return true;
    },
    {
      message: 'Please select a sender phone number.',
      path: ['callerPhoneNumberId'],
    }
  );

/**
 * @deprecated use ConversationBlockConfigSchema
 */
export type ConversationBlockConfigDeprecated = z.infer<
  | typeof WaitUntilClosedConversationBlockConfigSchema
  | typeof NonBlockingConversationBlockConfigSchema
>;
